<template>
  <section class="sentence-explanation" :aria-label="$t('sentenceExplanation.title')" aria-live="polite">
    <div v-if="state.loading" class="skeleton" :aria-label="$t('sentenceExplanation.loading')">
      <span></span><span></span><span class="short"></span>
    </div>

    <div v-else-if="state.error" class="explanation-error">
      <p>{{ state.error }}</p>
      <button v-if="state.retryable" type="button" class="text-button" @click="$emit('generate')">{{ $t('sentenceExplanation.retry') }}</button>
    </div>

    <template v-else-if="explanation">
      <SentenceExplanationContent :explanation="explanation" :follow-ups="followUps" />

      <div v-if="state.followUpKind" class="follow-up-pending">
        <h3>{{ $t(`sentenceExplanation.followUps.${state.followUpKind}`) }}</h3>
        <div class="skeleton"><span></span><span class="short"></span></div>
      </div>
      <p v-if="state.followUpError" class="follow-up-error">{{ state.followUpError }}</p>

      <div v-if="openKinds.length" class="follow-up-bar">
        <h3>{{ $t('sentenceExplanation.followUpLabel') }}</h3>
        <button
          v-for="kind in openKinds"
          :key="kind"
          type="button"
          :disabled="Boolean(state.followUpKind)"
          @click="$emit('ask-follow-up', kind)"
        >
          <span>{{ $t(`sentenceExplanation.followUps.${kind}`) }}</span>
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6 3.5L10.5 8 6 12.5" /></svg>
        </button>
      </div>
    </template>

    <footer v-if="explanation || quota" class="explanation-footer">
      <button
        v-if="explanation"
        type="button"
        class="save-button"
        :class="{ saved: isSaved }"
        :aria-pressed="isSaved"
        :disabled="state.saving"
        @click="$emit('toggle-save')"
      >
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <path v-if="isSaved" d="M3.5 8.5l3 3 6-7" />
          <path v-else d="M8 3.5v9M3.5 8h9" />
        </svg>
        {{ isSaved ? $t('sentenceExplanation.savedToNotebook') : $t('sentenceExplanation.saveToNotebook') }}
      </button>
      <span class="footer-meta">
        <span v-if="quota">{{ $t('sentenceExplanation.quotaRemaining', { count: quota.remaining }) }}</span>
        <button
          v-if="explanation"
          type="button"
          class="text-button"
          :disabled="state.loading || Boolean(state.followUpKind)"
          @click="$emit('generate')"
        >
          {{ $t('sentenceExplanation.regenerate') }}
        </button>
      </span>
    </footer>
    <p v-if="state.saveError" class="follow-up-error">{{ state.saveError }}</p>
  </section>
</template>

<script>
import SentenceExplanationContent from './SentenceExplanationContent.vue'

export default {
  name: 'SentenceExplanationPanel',
  components: { SentenceExplanationContent },
  emits: ['generate', 'ask-follow-up', 'toggle-save'],
  props: {
    state: { type: Object, required: true },
    quota: { type: Object, default: null },
  },
  computed: {
    explanation() {
      return this.state.entry?.explanation || null
    },
    followUps() {
      return this.state.entry?.followUps || {}
    },
    openKinds() {
      return ['grammar', 'rephrase', 'examples']
        .filter((kind) => !this.followUps[kind] && kind !== this.state.followUpKind)
    },
    isSaved() {
      return Boolean(this.state.entry?.savedAt)
    },
  },
}
</script>

<style scoped>
.sentence-explanation { display: flex; flex-direction: column; gap: var(--space-8); }
.skeleton { display: grid; gap: 10px; padding-top: var(--space-1); }
.skeleton span { height: 10px; background: linear-gradient(90deg, var(--color-surface-muted) 0%, var(--color-border) 50%, var(--color-surface-muted) 100%); background-size: 200% 100%; border-radius: 3px; animation: skeleton-shimmer 1.3s linear infinite; }
.skeleton span.short { width: 58%; }
.explanation-error { display: grid; justify-items: start; gap: var(--space-2); }
.explanation-error p, .follow-up-error { margin: 0; color: var(--color-danger); font-size: 0.88rem; line-height: 1.6; }
.follow-up-pending { display: grid; gap: var(--space-3); }
h3 { margin: 0; color: var(--color-text-subtle); font-size: 0.75rem; font-weight: 500; letter-spacing: 0.02em; }
.follow-up-bar { display: grid; }
.follow-up-bar h3 { padding-bottom: var(--space-2); border-bottom: 1px solid var(--color-border); }
.follow-up-bar button { min-height: 44px; display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); padding: 0; color: var(--color-text); background: transparent; border: 0; border-bottom: 1px solid var(--color-border); cursor: pointer; font: inherit; font-size: 0.9rem; font-weight: 500; text-align: left; transition: color 180ms ease; }
.follow-up-bar svg { width: 14px; height: 14px; fill: none; stroke: var(--color-text-subtle); stroke-width: 1.6; stroke-linecap: round; stroke-linejoin: round; transition: transform 280ms var(--ease-spring), stroke 180ms ease; }
.follow-up-bar button:hover:not(:disabled) { color: var(--color-brand-700); }
.follow-up-bar button:hover:not(:disabled) svg { stroke: var(--color-brand-700); transform: translateX(3px); }
.follow-up-bar button:disabled { color: var(--color-text-subtle); cursor: not-allowed; }
.explanation-footer { display: flex; align-items: center; justify-content: space-between; gap: var(--space-3); margin-top: calc(var(--space-2) * -1); color: var(--color-text-subtle); font-size: 0.75rem; }
.footer-meta { display: flex; align-items: center; gap: var(--space-3); margin-left: auto; }
.save-button { min-height: 34px; display: inline-flex; align-items: center; gap: 6px; padding: 0 var(--space-3); color: var(--color-text); background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-md); cursor: pointer; font: inherit; font-size: 0.8rem; font-weight: 500; transition: border-color 180ms ease, color 180ms ease; }
.save-button svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 1.6; stroke-linecap: round; stroke-linejoin: round; }
.save-button:hover:not(:disabled) { border-color: var(--color-text-subtle); }
.save-button.saved { color: var(--color-brand-700); }
.save-button:disabled { color: var(--color-text-subtle); cursor: not-allowed; }
.text-button { padding: 0; color: var(--color-brand-700); background: transparent; border: 0; cursor: pointer; font: inherit; font-size: 0.8rem; font-weight: 500; }
.text-button:hover:not(:disabled) { text-decoration: underline; text-underline-offset: 3px; }
.text-button:disabled { color: var(--color-text-subtle); cursor: not-allowed; }
button:focus-visible { outline: 2px solid var(--color-brand-700); outline-offset: 2px; }
@keyframes skeleton-shimmer { to { background-position: -200% 0; } }
@media (prefers-reduced-motion: reduce) {
  .skeleton span { animation: none; }
  .follow-up-bar button, .follow-up-bar svg, .save-button { transition: none; }
}
</style>
